import React, { useEffect, useMemo, useState, useCallback } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Leaf, Warning, CheckCircle, XCircle, MinusCircle } from "@phosphor-icons/react";

const CLAIM_META = {
  "100_percent_organic": { label: "100% Organic", tone: "emerald" },
  organic: { label: "Organic (95%+)", tone: "emerald" },
  made_with_organic: { label: "Made with Organic ___", tone: "amber" },
  none: { label: "No organic claim", tone: "stone" },
};

function Gauge({ pct }) {
  const clamped = Math.max(0, Math.min(100, pct || 0));
  const r = 80;
  const circumference = Math.PI * r; // half circle
  const filled = (clamped / 100) * circumference;
  const color = clamped >= 95 ? "#047857" : clamped >= 70 ? "#b45309" : "#a8a29e";
  const marker = (p) => {
    const angle = Math.PI * (1 - p / 100);
    const x = 100 + r * Math.cos(angle);
    const y = 100 - r * Math.sin(angle);
    return { x, y };
  };
  const m95 = marker(95);
  const m70 = marker(70);
  return (
    <svg viewBox="0 0 200 115" className="w-full max-w-sm mx-auto" data-testid="organic-gauge">
      <path d="M 20 100 A 80 80 0 0 1 180 100" fill="none" stroke="#e7e5e4" strokeWidth="14" strokeLinecap="round" />
      <path
        d="M 20 100 A 80 80 0 0 1 180 100"
        fill="none" stroke={color} strokeWidth="14" strokeLinecap="round"
        strokeDasharray={`${filled} ${circumference}`}
      />
      <circle cx={m70.x} cy={m70.y} r="3.5" fill="#b45309" />
      <circle cx={m95.x} cy={m95.y} r="3.5" fill="#047857" />
      <text x="100" y="88" textAnchor="middle" fontSize="30" fontWeight="700" fill="#1c1917">
        {clamped.toFixed(1)}%
      </text>
      <text x="100" y="106" textAnchor="middle" fontSize="10" fill="#78716c">
        organic content (salt &amp; water excluded)
      </text>
    </svg>
  );
}

function StatusIcon({ status }) {
  if (status === "organic") return <CheckCircle size={20} weight="fill" className="text-emerald-700 shrink-0" />;
  if (status === "excluded") return <MinusCircle size={20} weight="fill" className="text-stone-400 shrink-0" />;
  return <XCircle size={20} weight="fill" className="text-red-600 shrink-0" />;
}

export default function Organic() {
  const [recipes, setRecipes] = useState([]);
  const [recipeId, setRecipeId] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [pctDrafts, setPctDrafts] = useState({});

  useEffect(() => {
    api.get("/recipes").then((r) => {
      setRecipes(r.data || []);
      if ((r.data || []).length > 0) setRecipeId(r.data[0].id);
    }).catch(() => toast.error("Could not load your formulas."));
  }, []);

  const loadAnalysis = useCallback(async (id) => {
    if (!id) { setAnalysis(null); return; }
    setLoading(true);
    try {
      const r = await api.get(`/recipes/${id}/organic`);
      setAnalysis(r.data);
      const drafts = {};
      (r.data.ingredients || []).forEach((row) => {
        if (row.is_organic) drafts[row.ingredient_id] = row.organic_content_pct ?? 100;
      });
      setPctDrafts(drafts);
    } catch {
      toast.error("Could not calculate organic content.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAnalysis(recipeId); }, [recipeId, loadAnalysis]);

  const setOrganic = async (row, isOrganic, pct) => {
    setSavingId(row.ingredient_id);
    try {
      await api.put(`/ingredients/${row.ingredient_id}/organic`, {
        is_organic: isOrganic,
        organic_content_pct: pct ?? 100,
      });
      await loadAnalysis(recipeId);
    } catch (e) {
      const msg = e?.response?.data?.detail;
      if (typeof msg === "string") toast.error(msg);
    } finally {
      setSavingId(null);
    }
  };

  const claim = analysis ? (CLAIM_META[analysis.claim] || CLAIM_META.none) : null;
  const claimClasses = useMemo(() => {
    if (!claim) return "";
    if (claim.tone === "emerald") return "border-emerald-300 bg-emerald-50 text-emerald-900";
    if (claim.tone === "amber") return "border-amber-300 bg-amber-50 text-amber-900";
    return "border-stone-300 bg-stone-100 text-stone-700";
  }, [claim]);

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="text-xs uppercase tracking-[0.2em] text-stone-500">Certification readiness</div>
        <h1 className="text-4xl font-bold tracking-tight flex items-center gap-3">
          <Leaf size={34} weight="duotone" className="text-emerald-800" /> Organic
        </h1>
        <p className="text-stone-600 mt-2 max-w-2xl">
          USDA National Organic Program content math for your formulas. Salt and water are
          excluded from the calculation entirely, and every organic ingredient is weighted
          by its own organic content — multi-ingredient organics are usually 95–100%, not 100%.
        </p>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <label className="text-sm font-semibold text-stone-700" htmlFor="organic-recipe">Formula</label>
          <select
            id="organic-recipe" data-testid="organic-recipe-select"
            className="border border-stone-300 rounded-md px-3 py-2 text-sm bg-white min-w-[240px]"
            value={recipeId} onChange={(e) => setRecipeId(e.target.value)}
          >
            {recipes.length === 0 && <option value="">No formulas yet</option>}
            {recipes.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </div>

        {loading && <div className="mt-8 text-stone-500 text-sm">Calculating…</div>}

        {!loading && analysis && (
          <>
            <div className="grid md:grid-cols-2 gap-4 mt-8">
              <Card className="p-6 border-stone-200">
                <Gauge pct={analysis.organic_pct} />
                <div className="flex justify-center gap-5 text-[11px] text-stone-500 mt-2">
                  <span><span className="inline-block w-2 h-2 rounded-full bg-amber-700 mr-1" />70% — Made with Organic</span>
                  <span><span className="inline-block w-2 h-2 rounded-full bg-emerald-700 mr-1" />95% — Organic</span>
                </div>
              </Card>
              <Card className={`p-6 border ${claimClasses}`} data-testid="organic-claim-card">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold opacity-70">Label claim earned (draft)</div>
                <div className="text-2xl font-bold mt-1">{claim.label}</div>
                <div className="text-sm mt-2 leading-relaxed">{analysis.claim_note}</div>
                {analysis.gaps?.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {analysis.gaps.map((g) => (
                      <div key={g.target} className="text-sm rounded-md border border-current/20 bg-white/60 p-3">
                        <span className="font-semibold">Recommended Changes to Reach {g.target}:</span>{" "}
                        {g.suggested_swaps.length > 0 ? (
                          <>
                            swap or source certified-organic versions of (≈{g.shortfall_g} g of organic content needed):
                            <ul className="mt-1.5 space-y-1">
                              {g.suggested_swaps.map((s) => (
                                <li key={s.name ?? s} className="flex items-center gap-2">
                                  <span className="font-semibold">{s.name ?? s}</span>
                                  {s.gain_pct != null && (
                                    <span
                                      className={`text-xs font-bold rounded-full px-2 py-0.5 border ${
                                        s.gain_pct >= 20
                                          ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                                          : s.gain_pct >= 5
                                            ? "text-amber-700 bg-amber-50 border-amber-300"
                                            : "text-stone-600 bg-stone-100 border-stone-300"
                                      }`}
                                      title={
                                        s.gain_pct >= 20 ? "High impact" : s.gain_pct >= 5 ? "Medium impact" : "Low impact"
                                      }
                                    >
                                      +{s.gain_pct}%
                                    </span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </>
                        ) : (
                          <>≈{g.shortfall_g} g more organic content needed.</>
                        )}
                      </div>
                    ))}
                    <div className="text-xs opacity-80 flex flex-wrap items-center gap-x-3 gap-y-1 pt-1">
                      <span className="font-semibold">Impact key:</span>
                      <span>🟢 High impact (20%+)</span>
                      <span>🟡 Medium (5–20%)</span>
                      <span>⚪ Low (&lt;5%)</span>
                    </div>
                  </div>
                )}
              </Card>
            </div>

            <Card className="p-0 border-stone-200 mt-6 overflow-hidden">
              <div className="px-5 py-4 border-b border-stone-200 bg-stone-50 font-semibold">Ingredient breakdown</div>
              <div className="divide-y divide-stone-100">
                {analysis.ingredients.map((row) => (
                  <div key={row.ingredient_id} className="px-5 py-3 flex flex-wrap items-center gap-3" data-testid={`organic-row-${row.ingredient_id}`}>
                    <StatusIcon status={row.status} />
                    <div className="flex-1 min-w-[160px]">
                      <div className="font-medium">{row.declaration}</div>
                      <div className="text-xs text-stone-500">
                        {row.grams} g{row.excluded ? " · Excluded per USDA NOP (salt/water)" : row.is_organic ? ` · counts ${row.counted_organic_g} g organic` : " · not counted as organic"}
                      </div>
                    </div>
                    {!row.excluded && (
                      <div className="flex items-center gap-2">
                        {row.is_organic && (
                          <label className="text-xs text-stone-600 flex items-center gap-1">
                            organic content
                            <input
                              type="number" min="0" max="100" step="0.1"
                              className="w-16 border border-stone-300 rounded px-1.5 py-1 text-xs"
                              value={pctDrafts[row.ingredient_id] ?? 100}
                              data-testid={`organic-pct-${row.ingredient_id}`}
                              onChange={(e) => setPctDrafts((d) => ({ ...d, [row.ingredient_id]: e.target.value }))}
                              onBlur={(e) => {
                                const v = Math.max(0, Math.min(100, parseFloat(e.target.value) || 0));
                                if (v !== row.organic_content_pct) setOrganic(row, true, v);
                              }}
                            />%
                          </label>
                        )}
                        <button
                          disabled={savingId === row.ingredient_id}
                          data-testid={`organic-toggle-${row.ingredient_id}`}
                          onClick={() => setOrganic(row, !row.is_organic, pctDrafts[row.ingredient_id] ?? 100)}
                          className={`text-xs font-semibold rounded-full px-3 py-1.5 border transition ${
                            row.is_organic
                              ? "border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                              : "border-stone-300 bg-white text-stone-600 hover:bg-stone-50"
                          }`}
                        >
                          {savingId === row.ingredient_id ? "Saving…" : row.is_organic ? "Certified organic ✓" : "Mark organic"}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
                {analysis.ingredients.length === 0 && (
                  <div className="px-5 py-6 text-sm text-stone-500">This formula has no ingredients yet.</div>
                )}
              </div>
            </Card>

            <div className="rounded-lg border border-amber-300 bg-amber-100 text-amber-900 p-4 mt-6 flex gap-3">
              <Warning size={22} weight="fill" className="shrink-0" />
              {analysis.disclaimer_lines?.length > 0 ? (
                <div className="text-sm">
                  <div className="font-bold uppercase tracking-wide text-xs mb-1.5">
                    {analysis.disclaimer_title || "Liability Disclaimer"}
                  </div>
                  <ul className="space-y-1 list-disc pl-4">
                    {analysis.disclaimer_lines.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="text-sm">{analysis.disclaimer}</div>
              )}
            </div>
          </>
        )}

        {!loading && !analysis && recipes.length === 0 && (
          <Card className="p-8 mt-8 border-stone-200 text-center text-stone-600">
            Create a formula in the Formulation Builder first — then come back here to check its organic readiness.
          </Card>
        )}
      </div>
    </Layout>
  );
}
